import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * What to call an extractor on screen.
 *
 * `business_name` is what an operator typed; `name` is the machine-derived
 * technical string (service/entity set) that doubles as the delivery path
 * prefix. Every user-facing surface shows the former and falls back to the
 * latter, so an extractor nobody has named still reads as something rather
 * than as a blank.
 */
export function extractorLabel(extractor: {
  business_name?: string | null
  name: string
}): string {
  return extractor.business_name?.trim() || extractor.name
}

/**
 * Byte counts for reading, not for arithmetic — "12.4 MB", not "12958761".
 *
 * Used where the number is a magnitude an operator judges at a glance (how much
 * data is waiting to be delivered). The packets table deliberately still shows
 * raw counts, because there the exact figure is the point.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—"
  if (bytes < 1024) return `${bytes} B`
  const units = ["KB", "MB", "GB", "TB"]
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`
}
